import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  DoorOpen,
  Languages,
  TriangleAlert,
  Volume2,
  VolumeX,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  type BulkAlertResult,
  useAcknowledgeAlert,
  useBulkAcknowledgeAlerts,
  useBulkClearAlerts,
  useClearAlertWithReason,
  useUnresolvedAlerts,
} from "@/shared/api/hooks/alerts";
import { useSites } from "@/shared/api/hooks/sites";
import { serverClockSkewFromHeader } from "@/shared/api/hooks/weather";
import { qk } from "@/shared/api/keys";
import { useOrgAlertChannel } from "@/shared/api/ws";
import { selectUser, useAuth } from "@/shared/auth/store";
import { useCan } from "@/shared/auth/permissions";
import { useAlarmSettings } from "@/shared/hooks/useAlarmSettings";
import { useAlertTTS } from "@/shared/hooks/useAlertTTS";
import { changeLanguage, type Language } from "@/shared/i18n";
import {
  ALERT_LANGUAGES,
  alertSubjectName,
  buildSpokenAlert,
  LANG_LABEL,
  levelLabel,
} from "@/shared/i18n/alertPhrases";
import { stopAlarmTone } from "@/shared/lib/alarmTone";
import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/Button";
import { useToast } from "@/shared/ui/Toast";
import {
  bulkClearNeedsReason,
  ClearReasonPicker,
  DetailPanel,
  FeedCard,
  useClearReason,
} from "@/widgets/alerts";
import { DispatchBanner } from "@/widgets/EmergencyDispatchCenter";
import { WorkInsideAlarmLine } from "@/widgets/WorkInsideSummary";
import { useAlarmOverlayStore } from "@/shared/state/alarmOverlayStore";
import type { Alert } from "@/shared/api/models";
import { mixOr } from "@/shared/lib/cssSupport";
import { isBaselineExempt } from "@/shared/lib/alertBaseline";
import { type BroadcastState, summarizeBroadcast } from "@/shared/lib/broadcastStatus";
import { clearPushFocus, onPushFocus, peekPushFocus } from "@/shared/lib/pushFocus";
import { StatusDot, type StatusKind } from "@/shared/ui/StatusDot";

/**
 * AlarmCenter — the PWA critical-alarm surface, redesigned as a **master-detail**
 * full-screen overlay (in <AppShell>).
 *
 * A new active alert at/above the configured level opens the overlay (once per
 * alert id — module-level dedupe survives remounts) and fires the side-effects:
 * vibrate, auto voice (shared dedupe with the passive announcer so it never
 * double-speaks), and an OS Notification when the tab is backgrounded.
 *
 * Layout (vs. the old one-card-per-alert design):
 *   * top    — the selected alert's DETAIL (reuses AlertsPage's DetailPanel),
 *   * bottom — the LIST of every pending alert (reuses AlertsPage's FeedCard);
 *              new alerts **accumulate** here rather than replacing the card,
 *              each row carries its own 확인 / 해제 so the operator can retire
 *              them **one at a time** until the surface empties itself, and the
 *              list is never collapsed behind a "모두 보기" — every pending alert
 *              is reachable by scrolling,
 *   * toolbar — bulk 전체확인 / 전체해제, 음성 정지 (voice off; card stays),
 *               대피, and the voice-language quick-switch (also sets UI language).
 *
 * Clicking a row swaps the detail pane to that alert (master-detail), which is
 * the whole point of keeping the list on screen.
 *
 * The pending set is derived from the query (`state==="open" && !cleared_at &&
 * level>=threshold`), so accumulation + shrink happen for free: acknowledging
 * or clearing an alert drops it from this surface (it stays on the 경보 page),
 * and the last one closes the overlay. `shownAlarms`/seed only decides *when to
 * pop*, never what the list contains — no double-pop, no loop.
 *
 * **Geometry.** The surface is a panel **docked directly under the TopBar**, not
 * a card floating in the middle of the screen (사용자 요구: "상단 메뉴 바로 밑에
 * 배치"). It is deliberately *not* `inset-0`: it starts below the TopBar and
 * right of the Sidebar so the global chrome — 음성 토글, 언어 메뉴, 테마, 계정
 * 메뉴 — stays clickable while an alarm is up. Covering them was the single worst
 * usability bug of the previous design (no way to silence voice). On wide screens
 * it also leaves `--dispatch-dock-w` free on the right so the 119 승인 도크 stays
 * visible beside it instead of being buried underneath.
 */

// Overlay-open dedupe — module-level so remounts don't re-pop the same alert.
const shownAlarms = new Set<number>();

function clampLevel(n: number): 0 | 1 | 2 | 3 {
  if (n >= 3) return 3;
  if (n === 2) return 2;
  if (n === 1) return 1;
  return 0;
}

export function AlarmCenter() {
  const qc = useQueryClient();
  const user = useAuth(selectUser);
  const orgId = user?.organization?.id ?? null;
  // ack=사용자(operator) 이상, clear=사이트관리자 이상 (08 §3, D-R6).
  const canAck = useCan("operator");
  const canClear = useCan("safety_manager");
  const { t } = useTranslation();
  const navigate = useNavigate();

  const overlayEnabled = useAlarmSettings((s) => s.overlayEnabled);
  const vibrate = useAlarmSettings((s) => s.vibrate);
  const notifyBrowser = useAlarmSettings((s) => s.notifyBrowser);
  const threshold = useAlarmSettings((s) => s.threshold);
  const reshowRecent = useAlarmSettings((s) => s.reshowRecent);

  const tts = useAlertTTS();

  // 미해제 경보 전체. 무필터 `useAlerts()` 한 페이지(최신 50건)를 쓰면 경보가 몰릴 때
  // 아직 확인되지 않은 경보가 1페이지 밖으로 밀려 오버레이 목록·전체확인/전체해제 대상에서
  // 통째로 사라진다. 아래 `active` 의 판정식은 그대로다 — 입력 모집단만 바꾼다.
  const { alerts, ready } = useUnresolvedAlerts();
  const { data: sitesData } = useSites();
  const ack = useAcknowledgeAlert();
  const clear = useClearAlertWithReason();
  const ackBulk = useBulkAcknowledgeAlerts();
  const clearBulk = useBulkClearAlerts();
  const { toast } = useToast();

  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (sitesData?.results ?? []).forEach((s) => {
      if (s.id != null) m.set(s.id, s.name);
    });
    return (id: number | string | null | undefined) =>
      id == null ? "" : m.get(Number(id)) ?? "";
  }, [sitesData]);

  // `open` = overlay visible; `selectedId` = which alert shows in the detail
  // pane. Both decoupled from the derived active list so a new alert accumulates
  // in the list without clobbering the operator's current selection.
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // 119 승인 표면이 「오버레이 안 배너」와 「화면 아래 바」 중 어디에 붙을지 알려 준다.
  const setOverlayOpen = useAlarmOverlayStore((s) => s.setOpen);
  // On first load we adopt the standing backlog as already-seen so we only pop
  // for alerts that fire *after* mount (a genuine push), never the reload set.
  // 예외(lib/alertBaseline.ts, 10-12 검증 V-22): 푸시 알림으로 고른 경보, 설정을 켰으면 최근 미확인 경계 이상.
  const seededRef = useRef(false);
  /** 기준선이 흡수만 하고 띄우지 않은 경보 — 나중에 푸시로 고르면 여기서 빼 처음 뜨는 경보처럼 띄운다. */
  const absorbedRef = useRef<Set<number>>(new Set());
  /** 푸시 알림으로 고른 경보 — 콜드 스타트면 맡겨 둔 것, 앱이 살아 있으면 이벤트. 창을 열고 나면 비운다. */
  const [focusId, setFocusId] = useState<number | null>(() => peekPushFocus());
  // 해제 사유(widgets/alerts/ClearReasonPicker) — 지금 고른 경보(selected)에 묶는다. 고른 경보가 바뀌거나 창이 닫히면
  // 비운다(다음 경보 — 실경보 포함 — 에 지난 사유가 몰래 실리지 않게, 10-19 검증). 전체 해제는 2건 이상이면 필수(10-27).
  const {
    code: reasonCode,
    setCode: setReasonCode,
    text: reasonText,
    setText: setReasonText,
    reason: clearReason,
  } = useClearReason();

  // 앱이 살아 있는 동안 들어온 푸시 탭 — 그 경보로 옮기고(라우터, 전체 새로고침 없이), 창을 다시 연다.
  // 경보창을 꺼 둔(overlayEnabled=false) 단말도 화면 이동은 한다.
  useEffect(
    () =>
      onPushFocus((id) => {
        if (absorbedRef.current.delete(id)) shownAlarms.delete(id);
        setFocusId(id);
        clearPushFocus(id);
        navigate(`/alerts?id=${id}`);
      }),
    [navigate],
  );
  // 맡겨 둔 것은 첫 렌더에서 경보창·낭독기가 둘 다 상태로 옮겨 받았다 — 비워 둬야 나중에 새로고침해도 같은 경보를
  // 또 「푸시로 고른 경보」로 띄우지 않는다(낭독기는 같은 렌더에서 이미 읽었다).
  useEffect(() => {
    const id = peekPushFocus();
    if (id != null) clearPushFocus(id);
  }, []);

  // Refresh the alert feed the moment a new alert fires server-side.
  // ⚠️ `useAlertChannel(orgId, …)` 는 `site:<id>:alerts` 채널을 만든다(shared/api/ws.ts) —
  // 조직 id 를 사이트 채널에 꽂은 것이라 서버가 4403 으로 끊었고(재접속도 막힘) 이 갱신은
  // 죽어 있었다. 조직 채널 훅으로 바꾼다. 해제도 `alert.cleared` 로 실시간으로 온다(2026-10-01) —
  // 오버레이에서 해제된 경보를 바로 내린다. 30초 폴링은 WS 가 끊겼을 때의 보조 경로다.
  useOrgAlertChannel(orgId, (env) => {
    if (env.type === "alert.fired" || env.type === "alert.cleared") {
      qc.invalidateQueries({ queryKey: qk.safety.alerts.all });
    }
  });

  // Pending = still **unacknowledged**, not cleared, at/above threshold, newest
  // first. Drives the list column and the new-alert detection below. Ack (or
  // clear) removes the row here — that's the "하나씩 확인해서 없앤다" loop; the
  // full history (incl. acknowledged-but-active) lives on the 경보 page.
  const active = useMemo(
    () =>
      alerts
        .filter(
          (a) => a.state === "open" && !a.cleared_at && (a.level ?? 0) >= threshold,
        )
        .sort((a, b) => +new Date(b.raised_at) - +new Date(a.raised_at)),
    [alerts, threshold],
  );

  // Detect + fire side-effects for the newest qualifying, not-yet-shown alert.
  useEffect(() => {
    if (!overlayEnabled || !ready) return;

    // First loaded render: mark the existing backlog seen without popping.
    // 기준선 예외만 남겨 바로 아래에서 처음 뜨는 경보처럼 띄운다(창·진동·음성).
    if (!seededRef.current) {
      const now = Date.now();
      active.forEach((a) => {
        if (a.id == null || isBaselineExempt(a, { focusId, reshowRecent, now })) return;
        shownAlarms.add(a.id);
        absorbedRef.current.add(a.id);
      });
      seededRef.current = true;
    }

    // 푸시로 고른 경보가 이미 한 번 띄웠던 것이면(앱이 살아 있었다) 창만 다시 열고 그 경보를 고른다 — 다시 읽지 않는다.
    if (focusId != null && shownAlarms.has(focusId) && active.some((a) => a.id === focusId)) {
      setSelectedId(focusId);
      setOpen(true);
      setFocusId(null);
      return;
    }

    const next =
      (focusId != null ? active.find((a) => a.id === focusId && !shownAlarms.has(focusId)) : undefined) ??
      active.find((a) => a.id != null && !shownAlarms.has(a.id));
    if (!next || next.id == null) return;

    if (next.id === focusId) setFocusId(null);
    shownAlarms.add(next.id);
    setSelectedId(next.id);
    setOpen(true);

    // Vibrate (buzz harder for 심각). 설비 무응답(health)은 가스 위험이 아니므로 짧은 패턴.
    const isHealth = next.kind === "health";
    if (vibrate && typeof navigator !== "undefined" && "vibrate" in navigator) {
      try {
        navigator.vibrate(
          !isHealth && (next.level ?? 0) >= 3 ? [300, 150, 300, 150, 500] : [200, 120, 200],
        );
      } catch {
        /* ignore — visual alarm still shows */
      }
    }

    // Auto voice — shares dedupe with the passive announcer (no double-speak).
    // 경보음 → 음성 순서는 useAlertTTS.speakAlert 가 묶는다(여기서 따로 울리지 않는다).
    tts.announceOne(
      {
        id: next.id, level: next.level ?? 0, site: next.site, gas: next.gas,
        kind: next.kind, is_drill: next.is_drill, subject_key: next.subject_key,
      },
      (id) => siteName(id),
    );

    // OS notification when the operator isn't looking at the tab.
    if (
      notifyBrowser &&
      typeof Notification !== "undefined" &&
      Notification.permission === "granted" &&
      typeof document !== "undefined" &&
      document.hidden
    ) {
      try {
        const lvl = clampLevel(next.level ?? 0);
        // 가스면 가스 이름, 설비 무응답이면 「설비 무응답」 — 무응답을 「복합 가스」로 적지 않는다.
        // 센서 판독 모순(「sensor:…」, 기본 끔 K 경보)이면 「센서 점검 필요」(10-20).
        const subject = alertSubjectName(tts.lang, next.kind, next.gas, next.subject_key);
        const note = new Notification(`${levelLabel(tts.lang, lvl)} · ${subject}`, {
          body: buildSpokenAlert({
            lang: tts.lang, level: lvl, gas: next.gas, site: siteName(next.site),
            kind: next.kind, isDrill: next.is_drill, subjectKey: next.subject_key,
          }),
          tag: `expertgas-alarm-${next.id}`,
        });
        note.onclick = () => {
          window.focus();
          note.close();
        };
      } catch {
        /* notifications are best-effort */
      }
    }
    // Intentionally keyed on the active list; dedupe guarantees once-per-id.
    // focusId 는 푸시 탭 때 다시 돌리려고 둔다. reshowRecent 는 기준선을 잡는 첫 실행에서만 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, overlayEnabled, ready, focusId]);

  // Nothing active (everything cleared) → close.
  useEffect(() => {
    if (open && active.length === 0) setOpen(false);
  }, [open, active.length]);

  const visible = open && active.length > 0;
  useEffect(() => {
    setOverlayOpen(visible);
    return () => setOverlayOpen(false);
  }, [visible, setOverlayOpen]);
  useEffect(() => {
    if (visible) return;
    setReasonCode("");
    setReasonText("");
  }, [visible]);

  // The alert shown in the detail pane: the selection, else the newest active.
  const selected = useMemo<Alert | null>(
    () => active.find((a) => a.id === selectedId) ?? active[0] ?? null,
    [active, selectedId],
  );
  // 고른 경보가 바뀌면(목록에서 다른 행을 누름 · 한 건 해제로 다음 경보로 넘어감 · 새 경보가 떠 그쪽으로 옮겨 감) 사유를 비운다.
  const selectedKey = selected?.id ?? null;
  useEffect(() => {
    setReasonCode("");
    setReasonText("");
  }, [selectedKey]);

  if (!open || active.length === 0 || !selected) return null;

  // Overlay grade colour = the worst active level right now.
  const worst = clampLevel(active.reduce((m, a) => Math.max(m, a.level ?? 0), 0));
  const color = worst >= 3 ? "var(--danger)" : "var(--warning)";
  /** color-mix 를 모르는 보드(WebView 106)용 옅은 등급 색 — 등급 띠가 통째로 사라지지 않게. */
  const soft = worst >= 3 ? "var(--danger-soft)" : "var(--warning-soft)";

  // Single-alert 확인 / 해제. Both drop the row out of `active` (ack sets
  // state→acknowledged, clear sets cleared_at) so the surface shrinks one row
  // at a time and auto-closes on the last one. Feedback toasts carry a dedupe
  // key so ten rapid acks leave **one** line, not ten stacked cards.
  const busyId =
    (ack.isPending ? ack.variables?.id : undefined) ??
    (clear.isPending ? clear.variables?.id : undefined) ??
    null;
  const onAck = (id: number) =>
    ack.mutate(
      { id },
      {
        onSuccess: () =>
          toast({ title: t("pages.alerts.toast.acked"), tone: "success", dedupe: "alert-ack" }),
        onError: () =>
          toast({ title: t("pages.alerts.toast.ackFailed"), tone: "danger", dedupe: "alert-ack" }),
      },
    );
  // 한 건 해제 — 사유는 고른 경보(위 상세)를 해제할 때만 싣는다. 목록의 다른 행 [해제]에는 싣지 않는다.
  // 성공하면 비운다(창이 남아 있어도 다음 경보에 그대로 실리지 않게). 전체 해제는 한 번 고른 사유를 모두에 싣는다.
  const onClear = (id: number) => {
    const reason = id === selected.id ? clearReason : "";
    clear.mutate({ id, reason }, {
      onSuccess: () => {
        if (reason) {
          setReasonCode("");
          setReasonText("");
        }
        toast({ title: t("pages.alerts.toast.cleared"), tone: "success", dedupe: "alert-clear" });
      },
      onError: () =>
        toast({ title: t("pages.alerts.toast.clearFailed"), tone: "danger", dedupe: "alert-clear" }),
    });
  };

  // Bulk ack/clear over *every* active alert. One settled batch (bounded
  // concurrency, failures tallied) + a single feed invalidation — not the old
  // fire-and-forget per-alert `mutate()` loop that swallowed errors and
  // refetched N times. A completion toast reports any partial failure.
  const toastBulk = (okKey: string, failKey: string, res: BulkAlertResult) => {
    if (res.total === 0) return;
    if (res.failed === 0) {
      toast({ title: t(okKey, { n: res.ok }), tone: "success", dedupe: "alert-bulk" });
    } else {
      toast({
        title: t(failKey),
        description: t("pages.alerts.bulk.result", { ok: res.ok, failed: res.failed }),
        tone: "danger",
        dedupe: "alert-bulk",
      });
    }
  };
  const ackAll = async () => {
    const ids = active
      .filter((a) => a.id != null && a.state === "open")
      .map((a) => a.id!);
    if (ids.length === 0) return;
    toastBulk(
      "pages.alerts.bulk.ackedN",
      "pages.alerts.bulk.ackPartialFail",
      await ackBulk.mutateAsync(ids),
    );
  };
  // 여러 건을 한꺼번에 해제할 때는 사유 필수(SOP 2.4, 10-27) — 버튼이 막혀 있지만 단축 경로에서도 지킨다.
  const clearAllNeedsReason = bulkClearNeedsReason(active.length) && !clearReason;
  const clearAll = async () => {
    const ids = active.filter((a) => a.id != null).map((a) => a.id!);
    if (ids.length === 0 || (bulkClearNeedsReason(ids.length) && !clearReason)) return;
    const res = await clearBulk.mutateAsync({ ids, reason: clearReason });
    if (res.failed === 0) {
      setReasonCode("");
      setReasonText("");
    }
    toastBulk("pages.alerts.bulk.clearedN", "pages.alerts.bulk.clearPartialFail", res);
  };
  // Voice stop = cancel the current utterance now + suppress re-speak until the
  // operator re-enables (shared tts store → consistent with the TopBar toggle
  // and Settings). The visual card stays up; the header ✕ dismisses it.
  const stopVoice = () => {
    stopAlarmTone();
    if (tts.supported) {
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* ignore — the enabled=false effect also cancels */
      }
    }
    tts.setEnabled(false);
  };
  const dismiss = () => setOpen(false);
  const goEvacuate = () => {
    setOpen(false);
    navigate("/evacuation");
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-label={t("alarm.activeAria", {
        level: levelLabel(tts.lang, worst),
        n: active.length,
      })}
      className={cn(
        // NOT inset-0: the TopBar (음성 토글 · 언어 · 테마) and the Sidebar
        // (계정 메뉴) must stay reachable while an alarm is on screen.
        // ⚠️ fixed 는 셸의 상단 패딩을 받지 않는다. 상수 60px 로 두면 TopBar 가
        // 실제로 차지하는 [inset, inset+60] 구간을 z-90 으로 덮어, 위 주석이
        // 지키려던 **음성 토글·언어·테마가 경보 중에 눌리지 않는다**.
        // 사이드바는 lg 미만에서 숨으므로(hidden lg:flex) 좌측 비움도 lg 부터.
        // 하단은 탭바를 남겨 경보 중에도 경보·센서 탭으로 이동할 수 있게 한다.
        "fixed right-0 z-[90] top-[var(--chrome-top)]",
        "left-0 lg:left-[var(--sidebar-w)]",
        "bottom-[var(--tabbar-h)] lg:bottom-0",
        // Docked flush under the TopBar and stretched to fill the content area —
        // no centring, no floating-modal inset. 119 승인은 이 안의 배너(DispatchBanner)라
        // 오른쪽 레일을 비워 둘 필요가 없다(예전 도크는 폰에서 이 패널 밑에 묻혔다).
        "flex items-stretch",
      )}
      style={{
        background: mixOr("color-mix(in oklab, var(--bg) 78%, black 22%)", "var(--bg)"),
        backdropFilter: "blur(6px)",
        animation: "fade-in var(--motion-fast) var(--ease-out-soft) both",
      }}
    >
      <div
        className="flex h-full w-full flex-col overflow-hidden border-t-[3px] text-[var(--fg)]"
        style={{
          borderColor: color,
          background: "var(--bg-elevated)",
          boxShadow: mixOr(`0 8px 24px -12px color-mix(in oklab, ${color} 45%, transparent)`, "var(--shadow-3)"),
        }}
      >
        {/* ── grade band ─────────────────────────────────────────────── */}
        <div
          className="flex items-center gap-3 px-6 py-4"
          style={{ background: mixOr(`color-mix(in oklab, ${color} 16%, transparent)`, soft) }}
        >
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
            style={{ background: color, color: "white" }}
          >
            <TriangleAlert size={24} strokeWidth={2.25} />
          </span>
          <div className="min-w-0">
            <div className="text-[12px] font-medium uppercase tracking-[0.08em] text-[var(--fg-subtle)]">
              {t("alarm.newAlert")}
            </div>
            <div
              className="text-[24px] font-bold leading-tight tracking-[-0.02em]"
              style={{ color }}
            >
              {t("alarm.activeCount", {
                level: levelLabel(tts.lang, worst),
                n: active.length,
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("alarm.dismiss")}
            className="ml-auto grid h-9 w-9 place-items-center rounded-full text-[var(--fg-subtle)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* ── 119 발신 승인 — 대기 건이 있을 때만, 다른 무엇보다 위 ────── */}
        <DispatchBanner />

        {/* ── bulk toolbar ───────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--separator)] px-6 py-3">
          {canAck && (
            <Button
              intent="primary"
              size="sm"
              onClick={ackAll}
              loading={ackBulk.isPending}
              disabled={ackBulk.isPending || !active.some((a) => a.state === "open")}
              leading={<CheckCircle2 size={15} strokeWidth={1.9} />}
            >
              {t("pages.alerts.ackAll")}
            </Button>
          )}
          {canClear && (
            <Button
              intent="outline"
              size="sm"
              onClick={clearAll}
              loading={clearBulk.isPending}
              disabled={clearBulk.isPending || clearAllNeedsReason}
              title={clearAllNeedsReason ? t("alarm.clearReason.bulkRequired") : undefined}
              leading={<XCircle size={15} strokeWidth={1.9} />}
            >
              {t("pages.alerts.clearAll")}
            </Button>
          )}
          {/* 해제 사유 — 고르면 위에 고른 경보의 해제·전체 해제에 함께 기록된다. 전체 해제(2건 이상)는 필수. */}
          {canClear && (
            <ClearReasonPicker
              code={reasonCode}
              onCode={setReasonCode}
              text={reasonText}
              onText={setReasonText}
            />
          )}
          {canClear && clearAllNeedsReason && (
            <span className="text-[12px] text-[var(--fg-subtle)]">{t("alarm.clearReason.bulkRequired")}</span>
          )}
          <Button
            intent="ghost"
            size="sm"
            onClick={() => (tts.enabled ? stopVoice() : tts.setEnabled(true))}
            leading={
              tts.enabled ? (
                <VolumeX size={15} strokeWidth={1.9} />
              ) : (
                <Volume2 size={15} strokeWidth={1.9} />
              )
            }
          >
            {tts.enabled ? t("pages.alerts.voiceStop") : t("pages.alerts.voiceOn")}
          </Button>
          {/* 대피 안내는 가스 경보가 있을 때만 — 설비 무응답만 떠 있을 때 「대피」 버튼을 내밀지 않는다. */}
          {active.some((a) => a.kind !== "health") && (
            <Button
              intent="ghost"
              size="sm"
              onClick={goEvacuate}
              leading={<DoorOpen size={15} strokeWidth={1.9} />}
            >
              {t("alarm.evacuate")}
            </Button>
          )}

          {/* voice language quick-switch (also sets the app UI language) */}
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Languages
              size={14}
              className="mr-0.5 text-[var(--fg-subtle)]"
              strokeWidth={1.75}
            />
            {ALERT_LANGUAGES.map((lng) => {
              const isActive = tts.lang === lng;
              return (
                <button
                  key={lng}
                  type="button"
                  onClick={() => {
                    tts.setLangOverride(lng as Language);
                    changeLanguage(lng as Language);
                  }}
                  aria-pressed={isActive}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[12px] font-medium transition-colors",
                    isActive
                      ? "bg-[var(--accent)] text-white"
                      : "bg-[var(--bg-subtle)] text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]",
                  )}
                >
                  {LANG_LABEL[lng]}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── master-detail body ─────────────────────────────────────── */}
        {/* Two independently scrolling rows, not one long scroll: the detail
            stays pinned on top so a row click has a *visible* effect, and the
            list underneath keeps its own scrollbar so every pending alert is
            reachable without a "모두 보기" toggle. */}
        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 py-5">
          {/* detail (top) — the selected alert */}
          <div className="max-h-[45%] shrink-0 overflow-y-auto">
            <BroadcastStatusLine alert={selected} />
            {/* 작업 입실 중(2026-10-27) — 고른 가스 경보의 현장에 현장 PC 입실 기록이 남아 있는가(위치 아님). 관제 역할만. */}
            {canAck && selected.kind !== "health" && <WorkInsideAlarmLine siteId={selected.site} />}
            <DetailPanel
              alert={selected}
              onAck={onAck}
              onClear={onClear}
              canAck={canAck}
              canClear={canClear}
            />
          </div>

          {/* pending list (bottom) — new alerts accumulate, 확인/해제 removes */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-2 flex shrink-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-subtle)]">
                {t("alarm.activeList")}
              </span>
              <span className="text-[11.5px] text-[var(--fg-subtle)]">
                {t("alarm.listHint")}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <FeedCard
                isLoading={false}
                isError={false}
                list={active}
                selectedId={selected.id ?? null}
                onSelect={setSelectedId}
                onAck={onAck}
                onClear={onClear}
                canAck={canAck}
                canClear={canClear}
                busyId={busyId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const BROADCAST_TONE: Record<BroadcastState, StatusKind> = {
  queued: "info",
  done: "ok",
  error: "critical",
  unsupported: "caution",
  stale: "caution",
  cancelled: "offline",
  suppressed: "offline",
  unknown: "offline",
};

/**
 * 현장 방송 한 줄 — 고른 경보의 현장 스피커 방송이 어떻게 됐는지(2026-10-19 · 10-12 검증 V-20/V-22).
 *
 * 서버 원장(`tts`·`tts:<lang>`)을 읽기만 한다(lib/broadcastStatus.ts). 현장 방송 기록이 없는 경보는 그리지 않는다.
 * PC 스피커·IP 스피커 칸은 **매니져 ack 가 출력별 결과를 실어 줄 때만** 나온다 — 지금 매니져는 PC 결과만 보내므로
 * 「방송 완료」는 PC 기준이다(IP 결과는 P1-12 매니져 작업 뒤). 「방송 중」은 120초가 지나도 응답이 없으면 「응답 없음」.
 * 그 창은 서버 시각으로 잰다 — 응답 Date 헤더로 잰 시계 차가 있으면 보정하고, 없는데 적재 시각이 기기 시계보다 미래면
 * 「확인 불가」(10-20, F1 검증 잔존 결함 2).
 * 창이 떠 있는 동안만 그려지므로 5초 시계도 그때만 돈다.
 */
function BroadcastStatusLine({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const h = window.setInterval(() => setNow(Date.now()), 5_000);
    return () => window.clearInterval(h);
  }, []);
  const s = summarizeBroadcast(alert.delivery, now, serverClockSkewFromHeader());
  if (!s) return null;
  return (
    <div
      role="status"
      className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-[var(--fg-muted)]"
    >
      <span className="inline-flex items-center gap-1.5">
        <StatusDot kind={BROADCAST_TONE[s.state]} size={7} pulse={s.state === "queued"} />
        <span className="font-medium text-[var(--fg)]">{t("alarm.broadcast.label")}</span>
        <span>{t(`alarm.broadcast.state.${s.state}`)}</span>
      </span>
      {s.langsTotal > 1 && (
        <span className="tabular-nums text-[var(--fg-subtle)]">
          {t("alarm.broadcast.langs", { done: s.langsDone, total: s.langsTotal })}
        </span>
      )}
      {s.pc && (
        <span>
          {t("alarm.broadcast.pc")} · {t(`alarm.broadcast.out.${s.pc}`)}
        </span>
      )}
      {s.ip && (
        <span>
          {t("alarm.broadcast.ip")} · {t(`alarm.broadcast.out.${s.ip}`)}
        </span>
      )}
    </div>
  );
}
